'use client';

import {
  Button,
  ChevronIcon,
  CopyButton,
  DropdownButton,
  AccountIcon,
  PrivyIcon,
  CheckIcon,
} from 'mode-shared/components';
import { usePathname } from 'next/navigation';
import { useEffect, useRef, useMemo } from 'react';
import { useToggle } from 'react-use';
import { useOnClickOutside } from 'usehooks-ts';
import { formatWalletAddress } from 'mode-shared/utils/format';
import { useAccount, useDisconnect } from 'wagmi';
import {
  usePrivy,
  useWallets,
  useSetActiveWallet,
  useSolanaWallets,
  useConnectWallet,
  ConnectedWallet,
  ConnectedSolanaWallet,
} from 'mode-shared/context/WalletConnectorContext';
import { useWallet as useSolanaWallet } from '@solana/wallet-adapter-react';
import { WalletName as SolanaWalletName, StandardWalletAdapter } from '@solana/wallet-adapter-base';

export const WalletDropdown = () => {
  const { logout } = usePrivy();
  const { disconnect: disconnectEvm } = useDisconnect();
  const { setActiveWallet: setActiveEvmWallet } = useSetActiveWallet();
  const { select: setActiveSolanaWallet, disconnect: disconnectSolana } = useSolanaWallet();
  const handleConnectWallet = (wallet: ConnectedWallet | ConnectedSolanaWallet) => {
    switch (wallet.type) {
      case 'ethereum':
        setActiveEvmWallet(wallet);
        break;
      case 'solana':
        setActiveSolanaWallet(wallet.meta.name as SolanaWalletName);
        break;
      default:
        console.error('Invalid wallet type');
        break;
    }
  };
  const { connectWallet } = useConnectWallet({
    onSuccess: ({ wallet }) => {
      handleConnectWallet(wallet as ConnectedWallet | ConnectedSolanaWallet);
    },
  });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownButtonRef = useRef<HTMLDivElement>(null);
  const pathname = usePathname();
  const [isOpen, toggleIsOpen] = useToggle(false);
  const { wallets: evmWallets } = useWallets();
  const { wallets: solanaWallets } = useSolanaWallets();

  useEffect(() => {
    // close dropdown when account address or pathname changes
    if (isOpen) {
      toggleIsOpen(false);
    }
    // don't put isOpen as a dependency here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, toggleIsOpen]);

  const handleClickOutside = (event: MouseEvent | TouchEvent | FocusEvent) => {
    if (dropdownRef.current) {
      if (
        !dropdownRef.current.contains(event.target as Node) &&
        !dropdownRef?.current?.parentElement?.contains(event.target as Node)
      ) {
        toggleIsOpen(false);
      }
    }
  };

  const handleLogout = async () => {
    await logout();
    disconnectEvm();
    disconnectSolana();
  };

  useOnClickOutside(dropdownRef, handleClickOutside);

  return (
    <div className="relative">
      <DropdownButton className="relative" onClick={toggleIsOpen}>
        <div className="flex items-center gap-x-2" ref={dropdownButtonRef}>
          <PrivyIcon className="text-white" />
          <ChevronIcon className="hidden md:block" />
        </div>
      </DropdownButton>
      {isOpen && (
        <div
          ref={dropdownRef}
          className="flex flex-col gap-y-6 absolute right-0 -bottom-5 w-[320px] translate-y-[100%] rounded-xl border border-neutral-900 p-4 bg-neutral-950 shadow-xl text-neutral-600"
        >
          <p className="text-center text-sm text-neutral-100 font-medium font-sans">You are loggen in with Privy</p>
          <div className="flex flex-col gap-y-4">
            <p className="text-center text-sm text-neutral-100 font-medium font-sans">EVM Wallets</p>
            {evmWallets.length > 0 ? (
              evmWallets.map((wallet) => (
                <WalletItem onConnectWallet={() => handleConnectWallet(wallet)} key={wallet.address} wallet={wallet} />
              ))
            ) : (
              <p className="text-center text-xs text-neutral-400 font-medium">No wallets connected</p>
            )}
          </div>
          <div className="flex flex-col gap-y-4">
            <p className="text-center text-sm text-neutral-100 font-medium font-sans">Solana Wallets</p>
            {solanaWallets.length > 0 ? (
              solanaWallets.map((wallet) => (
                <WalletItem onConnectWallet={() => handleConnectWallet(wallet)} key={wallet.address} wallet={wallet} />
              ))
            ) : (
              <p className="text-center text-xs text-neutral-400 font-medium">No wallets connected</p>
            )}
          </div>
          <div className="flex flex-col gap-y-2">
            <Button size="sm" onClick={connectWallet}>
              Connect another Wallet
            </Button>
            <Button variant="secondary" size="sm" onClick={handleLogout}>
              Logout
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

const WalletItem = ({ wallet, onConnectWallet }: { wallet: any; onConnectWallet: (wallet: any) => void }) => {
  const { address: activeEvmWalletAddress } = useAccount();
  const { wallet: activeSolanaWallet } = useSolanaWallet();

  const isActive = useMemo(() => {
    switch (wallet.type) {
      case 'ethereum':
        return activeEvmWalletAddress === wallet.address;
      case 'solana':
        return (activeSolanaWallet?.adapter as StandardWalletAdapter)?.wallet?.accounts.some(
          (account) => account.address === wallet.address
        );
      default:
        return false;
    }
  }, [wallet.type, activeEvmWalletAddress, activeSolanaWallet]);

  return (
    <div className="flex justify-between gap-x-2" key={wallet.address}>
      <div className="flex items-center gap-x-2">
        <AccountIcon walletIconSrc={wallet.meta?.icon} size={32} />
        <div>
          <p className="flex items-center gap-x-1 text-sm">
            {formatWalletAddress(wallet.address, 4, 4)}
            <CopyButton size="sm" copyValue={wallet.address} />
            <span className="text-[10px] capitalize leading-none text-white p-1 bg-neutral-900 rounded-sm">
              {wallet.type}
            </span>
          </p>
          <p className="text-xs">{wallet.meta?.name}</p>
        </div>
      </div>
      <div className="flex gap-x-2 text-neutral-100">
        {isActive ? (
          <p className="text-xs text-success-500">Active</p>
        ) : (
          <Button size="sm" onClick={onConnectWallet} variant="secondary">
            <CheckIcon height={16} width={16} />
          </Button>
        )}
      </div>
    </div>
  );
};
